import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild
} from '@angular/core';

import { Subscription, switchMap, takeUntil, tap } from 'rxjs';

import {
  DeviceService, IExtendedLink, ILinkMedia,
  IPushCardWithLayout,
  MarginEnum, PushSizeEnum,
  SdLibConfig, SdPushCardComponent,
  SdSpyTargetDirective,
  SizesEnum
} from '@seb/web-design-system/core';

import { ChatService, ComponentService, ImageService, TrackingService } from '../../../services';
import { AbstractComponent } from '../abstract-component.component';
import { WrapperComponent } from '../../../components/static/core';
import { PushCardComponentInterface } from '../../../models';
import { PushCardMapper } from '../../../mappers';
import { AppTypeEnum } from '../../../enums';

@Component({
  selector: 'wc-push-card',
  templateUrl: './push-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    WrapperComponent,
    SdPushCardComponent,
    SdSpyTargetDirective
  ]
})
export class PushCardComponent extends AbstractComponent<PushCardComponentInterface> implements OnInit, OnDestroy {
  @ViewChild('pushCardComponent', { read: ElementRef })
  pushCardComponent: ElementRef;

  promotionPushHasBeenViewed = false;
  size: PushSizeEnum;
  sizesEnum = SizesEnum;
  marginEnum = MarginEnum;
  pushSizeEnum = PushSizeEnum;
  pushWithLayout: IPushCardWithLayout;
  isPushPromo: boolean;
  promotionName: string;
  isMobile$ = false;

  private static instanceCounter = 0;
  public uniqueId: string;
  private skeletonUid: string;
  private mobileSubscription: Subscription;

  constructor(
    private componentService: ComponentService,
    private pushCardMapper: PushCardMapper,
    private cdRef: ChangeDetectorRef,
    public sdLibConfig: SdLibConfig,
    private trackingService: TrackingService,
    private imageService: ImageService,
    private deviceService: DeviceService,
    private chatService: ChatService
  ) {
    super();

    PushCardComponent.instanceCounter++;

    this.uniqueId = `PushCardComponent_${PushCardComponent.instanceCounter}`;
  }

  private skeletonInit(cmp: PushCardComponentInterface) {
    if (this.useLazyLoad) {
      this.pushWithLayout = this.pushCardMapper.toPushCardWithLayoutSkeleton(cmp);
    }
    this.size = this.pushCardMapper.mapPushCardSize(cmp.pushSize);
    this.cdRef.markForCheck();
  }

  ngOnInit(): void {
    this.subscribeToMobileChanges();
    this.subscribeToComponentChanges();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.mobileSubscription?.unsubscribe();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.promotionPushHasBeenViewed) {
      this.promotionPushHasBeenViewed = this.trackingService.checkingForPromoView(
        this.pushCardComponent,
        this.isPushPromo,
        this.promotionName
      );
    }
  }

  onVideoEndedClicked(event: { status: string; title: string }): void {
    if (event) {
      this.trackVideoClick(event);
    }
  }

  onVideoPlayedClicked(event: { status: string; title: string }): void {
    if (event) {
      this.trackVideoClick(event);
    }
  }

  onPromoClick(): void {
    this.trackingService.onPromoClick(this.isPushPromo, this.promotionName);
  }

  private subscribeToMobileChanges(): void {
    this.mobileSubscription = this.deviceService.isMobile
      .pipe(takeUntil(this.destroy$))
      .subscribe((isMobile) => {
        this.isMobile$ = isMobile;
        this.cdRef.markForCheck();
      });
  }

  private handleComponentChange(cmp: PushCardComponentInterface): void {
    if (cmp.uid !== this.skeletonUid) {
      this.skeletonUid = cmp.uid;
      this.skeletonInit(cmp);
    }
  }

  private subscribeToComponentChanges(): void {
    this.component$
      .pipe(
        tap((cmp) => this.handleComponentChange(cmp)),
        this.componentService.reloadComponentForArrayProperties(['pictos']),
        switchMap((cmp) => this.componentService.getLazyComponentData(cmp, 'links')),
        takeUntil(this.destroy$)
      )
      .subscribe((cmp) => this.updateComponent(cmp));
  }

  private updateComponent(cmp: PushCardComponentInterface): void {
    this.isPushPromo = cmp.isPushPromo ? JSON.parse(cmp.isPushPromo) : false;
    this.promotionName = cmp.headline;
    if (cmp.mediaContainer) {
      this.updateMediaComponent(cmp);
    } else {
      this.pushWithLayout = this.pushCardMapper.toPushCardWithLayout(cmp);
      this.setLoaded();
      this.cdRef.markForCheck();
    }
  }

  private updateMediaComponent(cmp: PushCardComponentInterface): void {
    this.imageService
      .getMediaFromContainer(cmp.mediaContainer)
      .pipe(takeUntil(this.destroy$))
      .subscribe((customMedia) => {
        this.pushWithLayout = this.pushCardMapper.toPushCardWithLayout(
          cmp,
          this.imageService.normalizeMedia(customMedia),
          this.isMobile$
        );
        this.setLoaded();
        this.cdRef.markForCheck();
      });
  }

  private getAppTypeFromUrl(url: string): string {
    for (const app in AppTypeEnum) {
      if (url.includes(app)) {
        return AppTypeEnum[app];
      }
    }
    return null;
  }

  onPushCardClicked(event: IExtendedLink | ILinkMedia): void {
    if (event?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    const appType = this.getAppTypeFromUrl(event.url);
    this.trackingService.sendEvent(appType ? 'clickdownloadApp' : 'clickCardFrames', {
      appType,
      componentName: event.linkName
    });
  }

  private trackVideoClick(event: any) {
    return this.trackingService.sendEvent('clickVideo', {
      videoZone: '',
      videoAction: event?.status,
      videoName: event?.title
    });
  }
}
